import { useMemo } from 'react';
import { allPermissions, mergeRolePermissions } from '../data/staff/permissions';
import { findRoleById } from '../data/staff/roles';
import { findStaffById, getStaffUsers } from '../data/staff/staffStore';
import { TOKEN_KEYS } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

/** findRoleById uses setRolesCache() from listRoles (filled on staff screens). */

const CURRENT_STAFF_KEY = 'utkarsh_current_staff_id';

export function getCurrentStaffId() {
  if (typeof window === 'undefined') return 'STF-001';
  try {
    const raw = localStorage.getItem(TOKEN_KEYS.staff);
    if (raw) {
      const staff = JSON.parse(raw);
      if (staff?.staffId) return staff.staffId;
    }
  } catch {
    // fall through
  }
  return localStorage.getItem(CURRENT_STAFF_KEY) || 'STF-001';
}

export function setCurrentStaffId(staffId) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(CURRENT_STAFF_KEY, staffId);
}

function readAuthStaff() {
  try {
    const raw = localStorage.getItem(TOKEN_KEYS.staff);
    if (raw) return JSON.parse(raw);
  } catch {
    return null;
  }
  return null;
}

function readAuthPermissions() {
  try {
    const raw = localStorage.getItem(TOKEN_KEYS.permissions);
    if (raw) return JSON.parse(raw);
  } catch {
    return null;
  }
  return null;
}

function readIsSuperAdmin() {
  return localStorage.getItem(TOKEN_KEYS.isSuperAdmin) === 'true';
}

/** Prefer logged-in auth staff; fall back to mock staff store. */
export function getCurrentStaff() {
  const authStaff = readAuthStaff();
  if (authStaff) return authStaff;
  return findStaffById(getCurrentStaffId()) || getStaffUsers()[0] || null;
}

export function getCurrentStaffPermissions() {
  if (readIsSuperAdmin()) {
    return allPermissions();
  }
  const authPerms = readAuthPermissions();
  if (authPerms && Object.keys(authPerms).length) return authPerms;

  const staff = getCurrentStaff();
  if (!staff?.roleIds?.length) return null;
  const roles = staff.roleIds.map((id) => findRoleById(id)).filter(Boolean);
  return mergeRolePermissions(roles);
}

export function hasStaffPermission(key) {
  if (readIsSuperAdmin()) return true;
  const perms = getCurrentStaffPermissions();
  if (!perms) return false;
  const [group, right] = key.split('.');
  return Boolean(perms[group]?.[right]);
}

/** Access staff list / detail pages. */
export function canAccessStaffManagement() {
  return hasStaffPermission('staff.management') || hasStaffPermission('staff.view');
}

/** View organisation-wide activity logs. */
export function canViewStaffActivities() {
  return (
    hasStaffPermission('staff.activities') || hasStaffPermission('staff.management')
  );
}

/** View the system configuration screen. */
export function canViewSystemConfig() {
  return hasStaffPermission('system.view') || hasStaffPermission('system.edit');
}

/** Change system configuration values. */
export function canEditSystemConfig() {
  return hasStaffPermission('system.edit');
}

/**
 * Hook version — prefers live AuthContext permissions over localStorage/mocks.
 */
export function useStaffAccess() {
  const { staff, permissions, isSuperAdmin, hasPermission, isAuthenticated } = useAuth();

  return useMemo(() => {
    const currentStaff = staff || (isAuthenticated ? null : getCurrentStaff());

    const has = (module, action) => {
      if (isSuperAdmin) return true;
      if (permissions && Object.keys(permissions).length) {
        return Boolean(permissions?.[module]?.[action]);
      }
      return hasPermission(module, action) || hasStaffPermission(`${module}.${action}`);
    };

    return {
      staff: currentStaff,
      permissions:
        permissions && Object.keys(permissions).length
          ? permissions
          : getCurrentStaffPermissions(),
      isSuperAdmin,
      hasPermission: has,
      canAccessStaffManagement: () => has('staff', 'management') || has('staff', 'view'),
      canViewStaffActivities: () => has('staff', 'activities') || has('staff', 'management'),
      canViewSystemConfig: () => has('system', 'view') || has('system', 'edit'),
      canEditSystemConfig: () => has('system', 'edit'),
    };
  }, [staff, permissions, isSuperAdmin, hasPermission, isAuthenticated]);
}
